/**
 * Plain-English finding title translation layer (UX simplification: "a non-technical IT manager
 * should be able to run a full pentest without reading documentation"). Every pattern below is
 * matched against a REAL finding title format already produced by
 * packages/pentest-modules/src/modules/*.ts or packages/kali-parsers/src/nmap.ts — not a guess at
 * what a title might look like. Returns null (never a fabricated guess) when nothing matches, so
 * callers fall back to showing the technical title as-is.
 */

interface PlainEnglishRule {
  test: RegExp;
  translate: (match: RegExpMatchArray) => string;
}

const RULES: PlainEnglishRule[] = [
  // session-auth-hygiene.ts: `Session-like cookie "${name}" missing ${missing.join(', ')}` — the
  // exact missing-flag combination varies (Secure, HttpOnly, SameSite=Strict|Lax, or a
  // combination), but the plain-English framing is the same regardless of which flags are absent.
  { test: /^Session-like cookie ".*" missing .+$/, translate: () => 'Login session not fully protected against theft' },

  // weak-tls.ts
  { test: /^Certificate expiring soon$/, translate: () => 'Security certificate expires soon — site will show warnings' },
  { test: /^Deprecated TLS protocol in use/, translate: () => 'Outdated encryption detected' },

  // web-crawl-mapping.ts: `Sensitive-looking path reachable: ${hit.label}` — only the two labels
  // explicitly given plain-English copy are translated; the other SENSITIVE_PATH_PATTERNS labels
  // (.git metadata, .env file, Spring Boot actuator, backup path) fall back to the technical title
  // rather than inventing copy that wasn't specified.
  { test: /^Sensitive-looking path reachable: admin panel$/, translate: () => 'Admin area is publicly accessible' },
  { test: /^Sensitive-looking path reachable: API schema endpoint$/, translate: () => 'API documentation is publicly exposed' },
  {
    test: /^Discovered (\d+) reachable route\(s\)$/,
    translate: (m) => `Mapped ${m[1]} pages on this website`,
  },

  // open-port-exposure.ts: `${service} (port ${port}) reachable from outside`; kali's nmap.ts
  // parser: `Open port ${portId}/${protocol}...` — two different real sources for the same
  // "sensitive service reachable" concept.
  { test: /\(port \d+\) reachable from outside$/, translate: () => 'Sensitive services exposed to the network' },
  { test: /^Open port \d+\/\w+/, translate: () => 'Sensitive services exposed to the network' },
];

export function getPlainEnglishTitle(technicalTitle: string): string | null {
  for (const rule of RULES) {
    const match = technicalTitle.match(rule.test);
    if (match) return rule.translate(match);
  }
  return null;
}
